// src/BuildTrackerAdminDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BuildTrackerAdminDashboard.css';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || ''
});

const BuildTrackerAdminDashboard = ({ jwtToken }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState({});
  const [configLoading, setConfigLoading] = useState(true);
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    state: 'all',
    search: '',
    sort: 'order_date_desc',
    hideCompleted: false
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // UI states
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Import functionality disabled after initial setup
  // const [importing, setImporting] = useState(false);
  // const [importResults, setImportResults] = useState(null);
  // const [showImportModal, setShowImportModal] = useState(false);

  // Helper function to get admin headers
  const getAdminHeaders = () => {
    const headers = {};
    
    // Check if this is Shopify admin access
    const urlParams = new URLSearchParams(window.location.search);
    const isShopifyAccess = urlParams.get('shop') && urlParams.get('shop').includes('.myshopify.com');
    
    if (isShopifyAccess) {
      // For Shopify admin access, use admin email header
      headers['x-admin-email'] = 'info@doctordripp.com';
      console.log('🛠️ Using Shopify admin authentication');
    } else if (window.location.hostname.includes('rewards.doctordripp.com')) {
      // For rewards portal access, use admin email header
      headers['x-admin-email'] = 'info@doctordripp.com';
    }
    
    // Also try JWT if available
    if (jwtToken) {
      headers.Authorization = `Bearer ${jwtToken}`;
    }
    
    return headers;
  };

  const stateInfo = {
    1: { title: "Order Placed", color: "#ff6b6b", icon: "📦" },
    2: { title: "Lights Opened", color: "#ff8c42", icon: "🔥" },
    3: { title: "Paint Complete", color: "#ffd23f", icon: "🎨" },
    4: { title: "Retrofit Complete", color: "#06d6a0", icon: "🔧" },
    5: { title: "Seal Test Passed", color: "#118ab2", icon: "✅" },
    6: { title: "Order Shipped", color: "#06d6a0", icon: "🚚" }
  };

  useEffect(() => {
    if (jwtToken) {
      fetchOrders();
      fetchConfig();
    }
  }, [jwtToken, filters, pagination.page]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await api.get(`/api/build-tracker?action=admin-orders&${params}`, {
        headers: getAdminHeaders()
      });

      if (response.data.success) {
        setOrders(response.data.orders);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        }));
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      setConfigLoading(true);
      const response = await api.get('/api/build-tracker?action=admin-config', {
        headers: getAdminHeaders()
      });

      if (response.data.success) {
        setConfig(response.data.config);
      }
    } catch (err) {
      console.error('Error fetching config:', err);
    } finally {
      setConfigLoading(false);
    }
  };

  const updateOrderState = async (orderId, newState, notes = '') => {
    try {
      setUpdating(true);
      const response = await api.post('/api/build-tracker?action=admin-orders', {
        order_id: orderId,
        state: newState,
        notes
      }, {
        headers: getAdminHeaders()
      });

      if (response.data.success) {
        await fetchOrders(); // Refresh the list
        return true;
      } else {
        throw new Error(response.data.error || 'Update failed');
      }
    } catch (err) {
      console.error('Error updating order:', err);
      alert(`Failed to update order: ${err.message}`);
      return false;
    } finally {
      setUpdating(false);
    }
  };

  const toggleAutoMode = async (orderId, currentAutoMode) => {
    try {
      setUpdating(true);
      const response = await api.post('/api/build-tracker?action=admin-orders', {
        order_id: orderId,
        auto_mode: !currentAutoMode
      }, {
        headers: getAdminHeaders()
      });

      if (response.data.success) {
        await fetchOrders(); // Refresh the list
      } else {
        throw new Error(response.data.error || 'Update failed');
      }
    } catch (err) {
      console.error('Error toggling auto mode:', err);
      alert(`Failed to toggle auto mode: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Import functionality disabled after initial setup
  /*
  const handleRetroactiveImport = async () => {
    try {
      setImporting(true);
      setImportResults(null);
      
      const response = await api.post('/api/build-tracker?action=admin-import', {}, {
        headers: getAdminHeaders()
      });

      if (response.data.success) {
        setImportResults(response.data);
        setShowImportModal(true);
        await fetchOrders(); // Refresh the orders list
      } else {
        throw new Error(response.data.error || 'Import failed');
      }
    } catch (err) {
      console.error('Error running import:', err);
      alert(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };
  */

  const handleOrderSelection = (orderId) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const showOrderDetails = (order) => {
    setSelectedOrderDetails(order);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrderDetails(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getProgressPercentage = (state) => {
    if (state <= 1) return 0;
    if (state >= 6) return 100;
    return ((state - 1) / 5) * 100;
  };

  if (!jwtToken) {
    return (
      <div className="build-tracker-admin">
        <div className="auth-required">
          <h3>Authentication Required</h3>
          <p>Please log in to access the Build Tracker Admin Dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="build-tracker-admin">
      <div className="admin-header">
        <h2>
          🛠️ Build Tracker Admin
          {new URLSearchParams(window.location.search).get('shop') && (
            <span style={{ fontSize: '0.6em', marginLeft: '1rem', color: '#06d6a0' }}>
              (Shopify Admin)
            </span>
          )}
        </h2>
        <div className="admin-actions">
          {/* Import button disabled after initial setup
          <button 
            onClick={handleRetroactiveImport}
            className="import-button"
            disabled={importing || loading}
          >
            {importing ? '📥 Importing...' : '📥 Import Existing Orders'}
          </button>
          */}
          <button 
            onClick={() => setShowConfigModal(true)}
            className="config-button"
          >
            ⚙️ Configuration
          </button>
          <button 
            onClick={fetchOrders}
            className="refresh-button"
            disabled={loading}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>State:</label>
          <select 
            value={filters.state} 
            onChange={(e) => handleFilterChange('state', e.target.value)}
          >
            <option value="all">All States</option>
            {Object.entries(stateInfo).map(([state, info]) => (
              <option key={state} value={state}>
                {info.icon} {info.title}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Order number, customer email, or name..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Sort:</label>
          <select 
            value={filters.sort} 
            onChange={(e) => handleFilterChange('sort', e.target.value)}
          >
            <option value="order_date_desc">Order Date (Newest)</option>
            <option value="order_date_asc">Order Date (Oldest)</option>
            <option value="state_asc">State (Low to High)</option>
            <option value="state_desc">State (High to Low)</option>
            <option value="customer_asc">Customer Name (A-Z)</option>
            <option value="customer_desc">Customer Name (Z-A)</option>
          </select>
        </div>

        <div className="filter-group filter-toggle">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={filters.hideCompleted}
              onChange={(e) => handleFilterChange('hideCompleted', e.target.checked)}
              className="toggle-checkbox"
            />
            <span className="toggle-slider"></span>
            Hide Completed Orders
          </label>
        </div>
      </div>

      {/* Orders Table */}
      <div className="orders-section">
        {loading ? (
          <div className="loading-container">
            <div className="build-tracker-admin-loading-spinner"></div>
            <p>Loading orders...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <h3>Error</h3>
            <p>{error}</p>
            <button onClick={fetchOrders} className="retry-button">
              Try Again
            </button>
          </div>
        ) : orders.length === 0 ? (
          <div className="no-orders">
            <h3>No Orders Found</h3>
            <p>No build tracker orders match your current filters.</p>
          </div>
        ) : (
          <>
            <div className="orders-table-container">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedOrders(new Set(orders.map(o => o.id)));
                          } else {
                            setSelectedOrders(new Set());
                          }
                        }}
                        checked={selectedOrders.size === orders.length && orders.length > 0}
                      />
                    </th>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Total</th>
                    <th>Current State</th>
                    <th>Progress</th>
                    <th>Mode</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} className={selectedOrders.has(order.id) ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedOrders.has(order.id)}
                          onChange={() => handleOrderSelection(order.id)}
                        />
                      </td>
                      <td>
                        <div className="order-info">
                          <strong 
                            className="order-number-link" 
                            onClick={() => showOrderDetails(order)}
                            title="Click to view details"
                          >
                            #{order.order_number}
                          </strong>
                          <div className="order-meta">
                            {order.days_in_progress} days in progress
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="customer-info">
                          <div>{order.customer_name || 'N/A'}</div>
                          <div className="customer-email">{order.customer_email}</div>
                        </div>
                      </td>
                      <td>
                        <div className="date-info">
                          {formatDate(order.order_date)}
                        </div>
                      </td>
                      <td>
                        <div className="total-info">
                          {formatCurrency(order.order_total)}
                        </div>
                      </td>
                      <td>
                        <div className="state-info">
                          <span 
                            className="state-badge"
                            style={{ backgroundColor: stateInfo[order.current_state]?.color }}
                          >
                            {stateInfo[order.current_state]?.icon} {order.current_state}
                          </span>
                          <div className="state-title">
                            {stateInfo[order.current_state]?.title}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="progress-info">
                          <div className="progress-bar-mini">
                            <div 
                              className="progress-fill-mini"
                              style={{ 
                                width: `${getProgressPercentage(order.current_state)}%`,
                                backgroundColor: stateInfo[order.current_state]?.color
                              }}
                            />
                          </div>
                          <div className="progress-text">
                            {getProgressPercentage(order.current_state)}%
                          </div>
                        </div>
                      </td>
                      <td>
                        <button
                          className={`mode-toggle ${order.auto_mode ? 'auto' : 'manual'}`}
                          onClick={() => toggleAutoMode(order.id, order.auto_mode)}
                          disabled={updating}
                          title={order.auto_mode ? 'Auto mode enabled' : 'Manual mode enabled'}
                        >
                          {order.auto_mode ? '🤖 AUTO' : '👤 MANUAL'}
                        </button>
                      </td>
                      <td>
                        <div className="order-actions">
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                const notes = prompt('Optional notes for this state change:');
                                updateOrderState(order.id, parseInt(e.target.value), notes || '');
                                e.target.value = ''; // Reset select
                              }
                            }}
                            disabled={updating}
                          >
                            <option value="">Change State</option>
                            {Object.entries(stateInfo).map(([state, info]) => (
                              <option 
                                key={state} 
                                value={state}
                                disabled={parseInt(state) === order.current_state}
                              >
                                {info.icon} {info.title}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              <div className="pagination-info">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} orders
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  Previous
                </button>
                
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  const pageNum = Math.max(1, pagination.page - 2) + i;
                  if (pageNum > pagination.pages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={pageNum === pagination.page ? 'active' : ''}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Configuration Modal */}
      {showConfigModal && (
        <ConfigModal
          config={config}
          onClose={() => setShowConfigModal(false)}
          onSave={fetchConfig}
          getAdminHeaders={getAdminHeaders}
        />
      )}

      {/* Import Results Modal - disabled after initial setup
      {showImportModal && importResults && (
        <ImportResultsModal
          results={importResults}
          onClose={() => setShowImportModal(false)}
        />
      )}
      */}

      {/* Bulk Actions */}
      {selectedOrders.size > 0 && (
        <div className="bulk-actions">
          <div className="bulk-info">
            {selectedOrders.size} order{selectedOrders.size !== 1 ? 's' : ''} selected
          </div>
          <div className="bulk-buttons">
            <button
              onClick={() => {
                const newState = prompt('Enter new state (1-6):');
                if (newState && newState >= 1 && newState <= 6) {
                  const notes = prompt('Optional notes for this bulk update:');
                  selectedOrders.forEach(orderId => {
                    updateOrderState(orderId, parseInt(newState), notes || '');
                  });
                  setSelectedOrders(new Set());
                }
              }}
              disabled={updating}
            >
              Bulk Update State
            </button>
            <button
              onClick={() => setSelectedOrders(new Set())}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {isModalOpen && selectedOrderDetails && (
        <OrderDetailsModal
          order={selectedOrderDetails}
          onClose={closeModal}
          onUpdateState={updateOrderState}
          updating={updating}
          allOrders={orders}
        />
      )}
    </div>
  );
};

// Configuration Modal Component
const ConfigModal = ({ config, onClose, onSave, getAdminHeaders }) => {
  const [tempConfig, setTempConfig] = useState(config);
  const [saving, setSaving] = useState(false);

  const handleSave = async (key, value) => {
    try {
      setSaving(true);
      const response = await api.post('/api/build-tracker?action=admin-config', {
        config_key: key,
        config_value: value
      }, {
        headers: getAdminHeaders()
      });

      if (response.data.success) {
        await onSave();
        setTempConfig(prev => ({ ...prev, [key]: value }));
      } else {
        throw new Error(response.data.error || 'Save failed');
      }
    } catch (err) {
      console.error('Error saving config:', err);
      alert(`Failed to save configuration: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="config-modal">
        <div className="modal-header">
          <h3>Build Tracker Configuration</h3>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        <div className="modal-content">
          <div className="config-item">
            <label>Tracker Enabled:</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={tempConfig.tracker_enabled === true || tempConfig.tracker_enabled === 'true'}
                onChange={(e) => handleSave('tracker_enabled', e.target.checked)}
                disabled={saving}
              />
              <span className="slider"></span>
            </label>
          </div>
          
          <div className="config-item">
            <label>State Progression Days:</label>
            <input
              type="text"
              value={Array.isArray(tempConfig.state_progression_days) ? tempConfig.state_progression_days.join(', ') : ''}
              onChange={(e) => {
                const days = e.target.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d));
                setTempConfig(prev => ({ ...prev, state_progression_days: days }));
              }}
              onBlur={(e) => {
                const days = e.target.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d));
                handleSave('state_progression_days', days);
              }}
              placeholder="0, 7, 14, 21, 28"
              disabled={saving}
            />
            <small>Days after order date for automatic state progression</small>
          </div>

          <div className="config-item">
            <label>Custom Built Tag:</label>
            <input
              type="text"
              value={tempConfig.custom_built_tag?.replace(/"/g, '') || ''}
              onChange={(e) => setTempConfig(prev => ({ ...prev, custom_built_tag: e.target.value }))}
              onBlur={(e) => handleSave('custom_built_tag', `"${e.target.value}"`)}
              placeholder="CustomBuilt"
              disabled={saving}
            />
            <small>Shopify product tag that triggers build tracking</small>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="cancel-button">Close</button>
        </div>
      </div>
    </div>
  );
};

// Import Results Modal Component - disabled after initial setup
/*
const ImportResultsModal = ({ results, onClose }) => {
  return (
    <div className="modal-overlay">
      <div className="import-results-modal">
        <div className="modal-header">
          <h3>📥 Import Results</h3>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        <div className="modal-content">
          <div className="import-summary">
            <h4>Summary</h4>
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-label">Total Orders Processed:</span>
                <span className="stat-value">{results.total}</span>
              </div>
              <div className="stat-item success">
                <span className="stat-label">✅ Successfully Imported:</span>
                <span className="stat-value">{results.imported}</span>
              </div>
              <div className="stat-item skipped">
                <span className="stat-label">⏭️ Already Existed:</span>
                <span className="stat-value">{results.skipped}</span>
              </div>
              <div className="stat-item no-custom">
                <span className="stat-label">❌ No CustomBuilt Items:</span>
                <span className="stat-value">{results.noCustomBuilt}</span>
              </div>
              {results.errors > 0 && (
                <div className="stat-item errors">
                  <span className="stat-label">💥 Errors:</span>
                  <span className="stat-value">{results.errors}</span>
                </div>
              )}
            </div>
          </div>

          {results.details && results.details.length > 0 && (
            <div className="import-details">
              <h4>Imported Orders</h4>
              <div className="details-list">
                {results.details.slice(0, 10).map((detail, index) => (
                  <div key={index} className="detail-item">
                    <span className="order-number">#{detail.order_number}</span>
                    <span className="order-state">State {detail.state}</span>
                    <span className="order-age">{detail.days_old} days old</span>
                    <span className="order-items">{detail.custom_built_items} items</span>
                  </div>
                ))}
                {results.details.length > 10 && (
                  <div className="more-items">
                    ... and {results.details.length - 10} more orders
                  </div>
                )}
              </div>
            </div>
          )}

          {results.imported > 0 && (
            <div className="success-message">
              <h4>🎉 Import Successful!</h4>
              <p>
                {results.imported} orders with CustomBuilt products have been imported into the build tracker.
                Customers can now enter their email to track their build progress!
              </p>
            </div>
          )}

          {results.imported === 0 && results.total > 0 && (
            <div className="no-imports-message">
              <h4>ℹ️ No New Orders Imported</h4>
              <p>
                {results.skipped > 0 && `${results.skipped} orders already existed in the build tracker. `}
                {results.noCustomBuilt > 0 && `${results.noCustomBuilt} orders didn't contain CustomBuilt products.`}
              </p>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="close-modal-button">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
*/

// Order Details Modal Component
const OrderDetailsModal = ({ order, onClose, onUpdateState, updating, allOrders }) => {
  const [selectedState, setSelectedState] = useState(order.current_state);
  const [orderItems, setOrderItems] = useState([]);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(order);

  const stateLabels = {
    1: 'Order Placed',
    2: 'Lights Opened', 
    3: 'Paint Complete',
    4: 'Retrofit Complete',
    5: 'Seal Test Passed',
    6: 'Order Shipped'
  };

  const stateDescriptions = {
    1: "We've received your order and are making preparations. If any parts need to be ordered, we'll do that first — otherwise expect your lights to be opened soon.",
    2: "Your lights have been cooked open and are being prepped for paint.",
    3: "Your lights have fully cured and are ready for retrofitting.",
    4: "All components are secured & tested and headlights are sealed. Next we complete the wiring harness and flood test the seal.",
    5: "Your headlights are almost ready! We're just packaging them up and doing a final check!",
    6: "Your order has been shipped and is on its way to you!"
  };

  const stateIcons = {
    1: '📦',
    2: '🔥', 
    3: '🎨',
    4: '🔧',
    5: '✅',
    6: '🚚'
  };

  const stateColors = {
    1: '#ff6b6b',
    2: '#ff8c42',
    3: '#ffd23f',
    4: '#06d6a0',
    5: '#118ab2',
    6: '#8338ec'
  };

  const getProgressPercentage = (state) => {
    return ((state - 1) / 5) * 100;
  };

  // Fetch customer orders and order items on mount
  useEffect(() => {
    const fetchData = async () => {
      // Get all orders for this customer
      const customerOrdersList = allOrders.filter(o => o.customer_email === currentOrder.customer_email);
      setCustomerOrders(customerOrdersList);
      
      // Get order items for current order
      try {
        const response = await api.get(`/api/build-tracker?action=order-items&order_id=${currentOrder.id}`);
        if (response.data.success) {
          setOrderItems(response.data.items || []);
        }
      } catch (error) {
        console.log('Could not fetch order items:', error);
        setOrderItems([]);
      }
    };
    
    fetchData();
  }, [currentOrder.id, currentOrder.customer_email, allOrders]);

  // Update selectedState when currentOrder changes
  useEffect(() => {
    setSelectedState(currentOrder.current_state);
  }, [currentOrder.current_state]);

  const handleOrderSelect = (selectedOrder) => {
    setCurrentOrder(selectedOrder);
  };

  const handleUpdateState = async () => {
    if (selectedState !== currentOrder.current_state) {
      const success = await onUpdateState(currentOrder.id, selectedState);
      if (success) {
        // Update the current order state locally
        setCurrentOrder(prev => ({ ...prev, current_state: selectedState }));
      }
    }
  };

  const progressPercent = getProgressPercentage(currentOrder.current_state);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="build-tracker-modal" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="modal-close">×</button>
        
        <div className="build-tracker-dashboard">
          <div className="dashboard-header">
            <h2>Build Tracker Dashboard</h2>
            <p>Track the progress of your custom-built orders</p>
          </div>
          
          <div className="dashboard-content">
            <div className="orders-sidebar">
              <h3>Your Orders</h3>
              <div className="orders-list">
                {customerOrders.map(customerOrder => {
                  const customerProgressPercent = getProgressPercentage(customerOrder.current_state);
                  const isActive = customerOrder.id === currentOrder.id;
                  
                  return (
                    <div 
                      key={customerOrder.id}
                      className={`order-card ${isActive ? 'active' : ''}`}
                      onClick={() => handleOrderSelect(customerOrder)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="order-header">
                        <span className="order-number">#{customerOrder.order_number}</span>
                        <span className={`state-badge state-${customerOrder.current_state}`}>
                          {stateIcons[customerOrder.current_state]} {stateLabels[customerOrder.current_state]}
                        </span>
                      </div>
                      <div className="order-meta">
                        <span className="order-date">{new Date(customerOrder.order_date).toLocaleDateString()}</span>
                        <span className="order-total">${parseFloat(customerOrder.order_total || 0).toFixed(2)}</span>
                      </div>
                      <div className="order-progress">
                        <div className="progress-bar-mini">
                          <div 
                            className="progress-fill-mini" 
                            style={{
                              width: `${customerProgressPercent}%`, 
                              backgroundColor: stateColors[customerOrder.current_state]
                            }}
                          ></div>
                        </div>
                        <span className="progress-text-mini">{Math.round(customerProgressPercent)}% Complete</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="admin-update-section">
                <label htmlFor="modal-state-select">Admin Update State:</label>
                <select 
                  id="modal-state-select"
                  value={selectedState} 
                  onChange={(e) => setSelectedState(parseInt(e.target.value))}
                  disabled={updating}
                >
                  {Object.entries(stateLabels).map(([state, label]) => (
                    <option key={state} value={state}>
                      {state}. {label}
                    </option>
                  ))}
                </select>
                
                {selectedState !== currentOrder.current_state && (
                  <button 
                    onClick={handleUpdateState}
                    disabled={updating}
                    className="update-state-btn"
                  >
                    {updating ? 'Updating...' : 'Update State'}
                  </button>
                )}
              </div>
            </div>
            
            <div className="order-detail">
              <div className="detail-content">
                <div className="detail-header">
                  <h3>Order #{currentOrder.order_number}</h3>
                  <div className="detail-meta">
                    <span>Placed: {new Date(currentOrder.order_date).toLocaleDateString()}</span>
                    <span>Total: ${parseFloat(currentOrder.order_total || 0).toFixed(2)}</span>
                    <span>Days in Progress: {currentOrder.days_in_progress}</span>
                  </div>
                </div>
                
                <div className="main-progress">
                  <div className="progress-title">
                    <h3>BUILD PROGRESS</h3>
                  </div>
                  <div className="current-state">
                    <div className="state-icon" style={{ color: stateColors[currentOrder.current_state] }}>
                      {stateIcons[currentOrder.current_state]}
                    </div>
                    <div className="state-info">
                      <h4 className="state-title-rainbow">{stateLabels[currentOrder.current_state]}</h4>
                      <p>{stateDescriptions[currentOrder.current_state]}</p>
                    </div>
                  </div>
                  <div className="progress-bar-main">
                    <div 
                      className="progress-fill-main" 
                      style={{
                        width: `${progressPercent}%`, 
                        background: `linear-gradient(90deg, ${stateColors[currentOrder.current_state]}, ${stateColors[currentOrder.current_state]}90)`
                      }}
                    ></div>
                  </div>
                  <div className="progress-percentage">{Math.round(progressPercent)}% Complete</div>
                </div>
                
                <div className="state-timeline">
                  <h4>Progress Timeline</h4>
                  <div className="timeline">
                    {Object.entries(stateLabels).map(([state, label]) => {
                      const stateNum = parseInt(state);
                      const isCompleted = stateNum <= currentOrder.current_state;
                      const isCurrent = stateNum === currentOrder.current_state;
                      const color = isCompleted ? stateColors[stateNum] : '#cccccc';
                      
                      // Get the actual timestamp for this state
                      let stateDate = 'Pending';
                      if (isCompleted) {
                        const stateTimestampField = `state_${stateNum}_at`;
                        const stateTimestamp = currentOrder[stateTimestampField];
                        
                        if (stateTimestamp) {
                          stateDate = new Date(stateTimestamp).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).replace(',', ' at');
                        } else {
                          // Fallback to order date for state 1, or show as pending
                          stateDate = stateNum === 1 ? new Date(currentOrder.order_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).replace(',', ' at') : 'Completed (date unknown)';
                        }
                      }
                      
                      return (
                        <div 
                          key={state}
                          className={`timeline-item ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
                        >
                          <div 
                            className="timeline-marker" 
                            style={{ backgroundColor: color }}
                          >
                            {stateIcons[stateNum]}
                          </div>
                          <div className="timeline-content">
                            <h5>{label}</h5>
                            <p className="timeline-date">
                              {stateDate}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="order-items">
                  <h4>Order Items</h4>
                  <div className="items-list">
                    {orderItems.length > 0 ? orderItems.map((item, index) => (
                      <div key={index} className="item-card">
                        <div className="item-info">
                          <h5>{item.product_title || item.title || 'Custom Product'}</h5>
                          <p className="item-variant">{item.variant_title || ''}</p>
                          <div className="item-meta">
                            <span>Qty: {item.quantity}</span>
                            <span>${parseFloat(item.price || 0).toFixed(2)}</span>
                            <span className="custom-built-badge">🛠️ Custom Built</span>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="item-card">
                        <div className="item-info">
                          <h5>Custom Built Product</h5>
                          <p className="item-variant">Custom configuration</p>
                          <div className="item-meta">
                            <span>Qty: 1</span>
                            <span>${parseFloat(currentOrder.order_total || 0).toFixed(2)}</span>
                            <span className="custom-built-badge">🛠️ Custom Built</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuildTrackerAdminDashboard;
